import React from 'react'
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  Handle,
  OnNodesChange,
  OnEdgesChange,
  ConnectionLineType,
  Position,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { Pencil, Sparkles } from 'lucide-react'
import type { ExpandedResult } from '../../types/promptcraft'

// Node styles
export const nodeStyles = {
  root: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: '2px solid #00d4ff',
    borderRadius: '12px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
  },
  branch: {
    background: '#1a2332',
    color: '#e0e0e0',
    border: '1px solid #00d4ff',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
  },
  leaf: {
    background: '#1a2332',
    color: '#b0b0b0',
    border: '1px solid #2a3a4a',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    maxWidth: '320px',
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    lineHeight: '1.5',
  },
}

// Editable leaf node component
function EditableLeafNode({ data, id }: { data: any; id: string }) {
  const { label, fullText, isEditing, borderColor, onStartEdit } = data

  return (
    <div
      style={{
        ...nodeStyles.leaf,
        borderColor: isEditing ? '#00d4ff' : (borderColor || '#2a3a4a'),
        borderWidth: isEditing ? '2px' : '1px',
        boxShadow: isEditing ? '0 0 8px rgba(0, 212, 255, 0.6)' : 'none',
        cursor: 'pointer',
        position: 'relative',
      }}
      onDoubleClick={() => onStartEdit(id, fullText)}
      title="双击编辑"
    >
      <Handle type="target" position={Position.Left} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>{label}</span>
        <Pencil style={{ width: '10px', height: '10px', color: '#555', flexShrink: 0 }} />
      </div>
    </div>
  )
}

export const nodeTypes = { editableLeaf: EditableLeafNode }

// Branch configuration
const BRANCHES = [
  { id: 'subject', label: '[主体]', color: '#00d4ff' },
  { id: 'style', label: '[风格]', color: '#f59e0b' },
  { id: 'lighting', label: '[光影]', color: '#f59e0b' },
  { id: 'camera', label: '[构图]', color: '#10b981' },
  { id: 'environment', label: '[氛围]', color: '#ec4899' },
  { id: 'mood_material', label: '[技术参数]', color: '#ec4899' },
]

const BRANCH_SPACING = 85

interface BuildMindMapParams {
  result: ExpandedResult
  expandedNodes: Set<string>
  editingNodeId: string | null
  savedPositions: Record<string, { x: number; y: number }>
  handleStartEdit: (nodeId: string, fullText: string) => void
}

export function buildMindMapLayout(params: BuildMindMapParams): { nodes: Node[]; edges: Edge[] } {
  const { result, expandedNodes, editingNodeId, savedPositions, handleStartEdit } = params
  if (!result.structured) return { nodes: [], edges: [] }

  const s = result.structured
  const newNodes: Node[] = []
  const newEdges: Edge[] = []

  const pos = (id: string, defaultX: number, defaultY: number) =>
    savedPositions[id] ?? { x: defaultX, y: defaultY }

  const totalBranchHeight = (BRANCHES.length - 1) * BRANCH_SPACING
  const branchStartY = -totalBranchHeight / 2

  // Root node
  newNodes.push({
    id: 'root',
    type: 'default',
    data: { label: '完整提示词' },
    position: pos('root', 0, 0),
    style: nodeStyles.root,
    sourcePosition: Position.Right,
    draggable: true,
  })

  // Branch nodes
  BRANCHES.forEach((branch, index) => {
    const y = branchStartY + index * BRANCH_SPACING
    const isExpanded = expandedNodes.has(branch.id)
    newNodes.push({
      id: branch.id,
      type: 'default',
      data: {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span>{isExpanded ? '▼' : '▶'}</span>
            <span>{branch.label}</span>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: branch.color,
              flexShrink: 0,
            }} />
          </div>
        )
      },
      position: pos(branch.id, 180, y),
      style: { ...nodeStyles.branch, borderColor: branch.color },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      draggable: true,
    })
    newEdges.push({
      id: `e-root-${branch.id}`,
      source: 'root',
      target: branch.id,
      style: { stroke: branch.color, strokeWidth: 2 },
      type: 'default',
    })
  })

  // Subject child nodes (3-level structure)
  const subjectFields = [
    { key: 'core_noun', label: '核心名词：', value: s.subject?.core_noun },
    { key: 'age_status_identity', label: '年龄/状态/身份：', value: s.subject?.age_status_identity },
    { key: 'appearance', label: '外貌关键特征：', value: s.subject?.appearance },
    { key: 'clothing_accessories', label: '服装/配饰：', value: s.subject?.clothing_accessories },
    { key: 'expression', label: '表情/微表情：', value: s.subject?.expression },
    { key: 'pose', label: '姿势/体态：', value: s.subject?.pose },
    { key: 'material_hint', label: '材质/质感初步暗示：', value: s.subject?.material_hint },
  ].filter(f => f.value)

  if (expandedNodes.has('subject')) {
    const subjectY = branchStartY
    const leafSpacing = 55
    const totalLeafHeight = (subjectFields.length - 1) * leafSpacing
    const leafStartY = subjectY - totalLeafHeight / 2

    subjectFields.forEach((field, index) => {
      const labelNodeId = `subject-label-${field.key}`
      const valueNodeId = `subject-value-${field.key}`
      const yPos = leafStartY + index * leafSpacing

      // Label node
      newNodes.push({
        id: labelNodeId,
        type: 'default',
        data: { label: field.label },
        position: pos(labelNodeId, 380, yPos),
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        draggable: true,
      })
      newEdges.push({
        id: `e-subject-${labelNodeId}`,
        source: 'subject',
        target: labelNodeId,
        style: { stroke: '#00d4ff', strokeWidth: 1.5 },
        type: 'default',
      })

      // Value node (editable)
      const isEditingThis = editingNodeId === valueNodeId
      newNodes.push({
        id: valueNodeId,
        type: 'editableLeaf',
        data: {
          label: field.value!,
          fullText: field.value!,
          isEditing: isEditingThis,
          borderColor: '#00d4ff',
          onStartEdit: handleStartEdit,
        },
        position: pos(valueNodeId, 580, yPos),
        draggable: true,
      })
      newEdges.push({
        id: `e-${labelNodeId}-${valueNodeId}`,
        source: labelNodeId,
        target: valueNodeId,
        style: { stroke: '#00d4ff', strokeWidth: 1.5 },
        type: 'default',
      })
    })
  }

  // Other branch content nodes
  const otherFields = [
    { parent: 'style', value: s.style },
    { parent: 'lighting', value: s.lighting },
    { parent: 'camera', value: s.camera },
    { parent: 'environment', value: s.environment },
    { parent: 'mood_material', value: s.mood_material },
  ]

  otherFields.forEach((field) => {
    if (field.value && expandedNodes.has(field.parent)) {
      const nodeId = `${field.parent}-content`
      const parentIndex = BRANCHES.findIndex(b => b.id === field.parent)
      const parentY = branchStartY + parentIndex * BRANCH_SPACING
      const parentColor = BRANCHES[parentIndex]?.color || '#2a3a4a'
      const isEditingThis = editingNodeId === nodeId

      newNodes.push({
        id: nodeId,
        type: 'editableLeaf',
        data: {
          label: field.value,
          fullText: field.value,
          isEditing: isEditingThis,
          borderColor: parentColor,
          onStartEdit: handleStartEdit,
        },
        position: pos(nodeId, 380, parentY),
        draggable: true,
      })
      newEdges.push({
        id: `e-${field.parent}-content`,
        source: field.parent,
        target: nodeId,
        style: { stroke: parentColor, strokeWidth: 1.5 },
        type: 'default',
      })
    }
  })

  return { nodes: newNodes, edges: newEdges }
}

// MindMapView component
interface MindMapViewProps {
  nodes: Node[]
  edges: Edge[]
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onNodeClick: (event: React.MouseEvent, node: Node) => void
  onNodeDragStart: (event: React.MouseEvent, node: Node) => void
  onNodeDrag: (event: React.MouseEvent, node: Node) => void
  onNodeDragStop: (event: React.MouseEvent, node: Node) => void
}

export function MindMapView({
  nodes, edges, onNodesChange, onEdgesChange,
  onNodeClick, onNodeDragStart, onNodeDrag, onNodeDragStop,
}: MindMapViewProps) {
  return (
    <div className="flex-1 bg-[#161b22] rounded-xl border border-cyan-900/30 overflow-hidden">
      {nodes.length > 0 ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          attributionPosition="bottom-left"
        >
          <Controls className="bg-[#161b22] border-cyan-900/30" />
          <Background color="#1a2332" gap={20} size={1} />
        </ReactFlow>
      ) : (
        <div className="h-full flex items-center justify-center text-gray-600">
          <div className="text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>输入创意种子，生成思维导图</p>
          </div>
        </div>
      )}
    </div>
  )
}
